import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedHeading from "../../animations/AnimatedHeading";

const testimonials = [
  {
    name: "Guy Hawkins",
    role: "Co. Founder",
    rating: 5,
    image: "https://randomuser.me/api/portraits/men/32.jpg",
    text: "Working with Tkmino has been a game-changer for our business. Their team's professionalism, attention to detail, and innovative solutions have helped us streamline operations and achieve our goals faster than we imagined. We truly feel like a valued partner. The results we've seen after to be our compnay partnering.",
  },
  {
    name: "Sarah Johnson",
    role: "CEO, TechCorp",
    rating: 4,
    image: "https://randomuser.me/api/portraits/women/44.jpg",
    text: "Working with Tkmino has been a game-changer for our business. Their team's professionalism, attention to detail, and innovative solutions have helped us streamline operations and achieve our goals faster than we imagined. We truly feel like a valued partner. The results we've seen after to be our compnay partnering.",
  },
  {
    name: "Mark Williams",
    role: "Product Manager",
    rating: 5,
    image: "https://randomuser.me/api/portraits/men/75.jpg",
    text: "Working with Tkmino has been a game-changer for our business. Their team's professionalism, attention to detail, and innovative solutions have helped us streamline operations and achieve our goals faster than we imagined. We truly feel like a valued partner. The results we've seen after to be our compnay partnering.",
  },
];

function Stars({ count, total = 5 }) {
  return (
    <div
      className="
        mb-5
        flex
        items-center
        justify-center
        gap-1
        max-[374px]:mb-4
      "
    >
      {Array.from({ length: total }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          fill={i < count ? "var(--brand-primary)" : "var(--border-primary)"}
          className="
            h-[17px]
            w-[17px]
            min-[375px]:h-[18px]
            min-[375px]:w-[18px]
            sm:h-[21px]
            sm:w-[21px]
            md:h-[22px]
            md:w-[22px]
            lg:h-[22px]
            lg:w-[22px]
          "
          aria-hidden="true"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [active, setActive] = useState(1);
  const len = testimonials.length;

  const prev = () => {
    setActive((p) => (p - 1 + len) % len);
  };

  const next = () => {
    setActive((p) => (p + 1) % len);
  };

  const getIndex = (offset) => (active + offset + len) % len;

  const startX = useRef(0);
  const endX = useRef(0);

  const handleTouchStart = (e) => {
    if (!e.touches?.length) return;

    startX.current = e.touches[0].clientX;
    endX.current = startX.current;
  };

  const handleTouchMove = (e) => {
    if (!e.touches?.length) return;

    endX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = startX.current - endX.current;

    if (Math.abs(distance) < 50) return;

    if (distance > 0) {
      next();
    } else {
      prev();
    }

    startX.current = 0;
    endX.current = 0;
  };

  return (
    <section
      className="
        relative
        w-full
        max-w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        py-10
        max-[374px]:py-9
        min-[375px]:py-12
        sm:py-16
        md:py-18
        lg:py-20
      "
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        touchAction: "pan-y",
      }}
    >
      {/* =========================================================
          SECTION HEADER
      ========================================================= */}

      <div
        className="
          mx-auto
          mb-8
          w-full
          max-w-7xl
          px-4
          text-center
          max-[374px]:mb-7
          min-[375px]:mb-9
          min-[375px]:px-5
          sm:mb-12
          sm:px-6
          md:px-8
          lg:mb-12
        "
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="
            mb-3
            text-[9px]
            font-bold
            uppercase
            tracking-[0.22em]
            text-[var(--brand-primary)]
            max-[374px]:text-[9px]
            min-[375px]:text-[11px]
            min-[375px]:tracking-[0.28em]
            sm:mb-4
            sm:text-xs
            sm:tracking-[0.3em]
            md:text-sm
          "
        >
          [ TESTIMONIALS ]
        </motion.p>

        <AnimatedHeading
          as="h2"
          parts={["What Our Clients Say"]}
          className="
            mx-auto
            w-full
            max-w-[340px]
            font-saira
            text-[23px]
            font-semibold
            leading-[1.2]
            text-[var(--text-primary)]
            max-[374px]:text-[22px]
            min-[375px]:max-w-none
            min-[375px]:text-[27px]
            sm:text-3xl
            md:text-4xl
            lg:text-6xl
          "
        />
      </div>

      {/* =========================================================
          AVATAR ROW
      ========================================================= */}

      <div
        className="
          relative
          mx-auto
          mb-0
          flex
          h-[68px]
          w-full
          max-w-xl
          items-center
          justify-center
          gap-0
          max-[374px]:h-[64px]
          min-[375px]:h-[74px]
          sm:h-[84px]
          md:h-[90px]
          lg:h-[90px]
        "
      >
        {/* Previous Avatar */}

        <motion.button
          type="button"
          onClick={prev}
          whileTap={{ scale: 0.93 }}
          aria-label="Previous testimonial"
          className="
            relative
            shrink-0
            cursor-pointer
            rounded-full
            border-0
            bg-transparent
            p-0
            outline-none
          "
          style={{ zIndex: 1 }}
        >
          <img
            src={testimonials[getIndex(-1)].image}
            alt={testimonials[getIndex(-1)].name}
            className="
              h-[44px]
              w-[44px]
              rounded-full
              object-cover
              max-[374px]:h-[42px]
              max-[374px]:w-[42px]
              min-[375px]:h-[50px]
              min-[375px]:w-[50px]
              sm:h-[60px]
              sm:w-[60px]
              md:h-[64px]
              md:w-[64px]
              lg:h-[64px]
              lg:w-[64px]
            "
            style={{
              border: "2px solid var(--border-primary)",
              filter: "brightness(0.55)",
              transition: "all .3s",
            }}
          />
        </motion.button>

        {/* Active Center Avatar */}

        <motion.button
          type="button"
          whileTap={{ scale: 0.96 }}
          onClick={next}
          key={active}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1],
          }}
          aria-label="Next testimonial"
          className="
            relative
            flex
            shrink-0
            flex-col
            items-center
            outline-none
            ml-[-6px]
            mr-[-6px]
            max-[374px]:ml-[-5px]
            max-[374px]:mr-[-5px]
            min-[375px]:ml-[-7px]
            min-[375px]:mr-[-7px]
            sm:ml-[-9px]
            sm:mr-[-9px]
            md:ml-[-10px]
            md:mr-[-10px]
            lg:ml-[-10px]
            lg:mr-[-10px]
          "
          style={{ zIndex: 2 }}
        >
          {/* Triangle Pointer */}

          <div
            className="
              absolute
              bottom-[-12px]
              left-1/2
              z-[3]
              -translate-x-1/2
              sm:bottom-[-14px]
            "
            style={{
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "10px solid var(--card-bg)",
            }}
          />

          <img
            src={testimonials[active].image}
            alt={testimonials[active].name}
            className="
              h-[58px]
              w-[58px]
              rounded-full
              object-cover
              max-[374px]:h-[54px]
              max-[374px]:w-[54px]
              min-[375px]:h-[64px]
              min-[375px]:w-[64px]
              sm:h-[74px]
              sm:w-[74px]
              md:h-[80px]
              md:w-[80px]
              lg:h-[80px]
              lg:w-[80px]
            "
            style={{
              border: "2.5px solid var(--brand-primary)",
              boxShadow:
                "0 0 0 3px color-mix(in srgb, var(--brand-primary) 15%, transparent)",
            }}
          />
        </motion.button>

        {/* Next Avatar */}

        <motion.button
          type="button"
          onClick={next}
          whileTap={{ scale: 0.93 }}
          aria-label="Next testimonial"
          className="
            relative
            shrink-0
            cursor-pointer
            rounded-full
            border-0
            bg-transparent
            p-0
            outline-none
          "
          style={{ zIndex: 1 }}
        >
          <img
            src={testimonials[getIndex(1)].image}
            alt={testimonials[getIndex(1)].name}
            className="
              h-[44px]
              w-[44px]
              rounded-full
              object-cover
              max-[374px]:h-[42px]
              max-[374px]:w-[42px]
              min-[375px]:h-[50px]
              min-[375px]:w-[50px]
              sm:h-[60px]
              sm:w-[60px]
              md:h-[64px]
              md:w-[64px]
              lg:h-[64px]
              lg:w-[64px]
            "
            style={{
              border: "2px solid var(--border-primary)",
              filter: "brightness(0.55)",
              transition: "all .3s",
            }}
          />
        </motion.button>
      </div>

      {/* =========================================================
          NAME + ROLE
      ========================================================= */}

      <AnimatePresence mode="wait">
        <motion.div
          key={active + "-name"}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          className="
            mt-4
            mb-6
            min-h-[42px]
            px-4
            text-center
            max-[374px]:mt-3
            max-[374px]:mb-5
            min-[375px]:mt-5
            sm:mb-8
            md:mt-6
            md:mb-6
            lg:mt-6
            lg:mb-6
          "
        >
          <p
            className="
              font-syne
              text-[13px]
              font-bold
              leading-tight
              text-[var(--text-primary)]
              min-[375px]:text-sm
              sm:text-base
            "
          >
            {testimonials[active].name}
          </p>

          <p
            className="
              mt-1
              font-dm
              text-[11px]
              text-[var(--text-muted)]
              min-[375px]:text-xs
              sm:text-sm
            "
          >
            {testimonials[active].role}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* =========================================================
          MAIN TESTIMONIAL AREA
      ========================================================= */}

      <div
        className="
          relative
          mx-auto
          flex
          w-full
          max-w-7xl
          items-center
          justify-center
          px-3
          max-[374px]:px-2
          min-[375px]:px-4
          sm:px-6
          md:px-8
          lg:px-4
        "
      >
        {/* =======================================================
            LEFT GHOST
        ======================================================= */}

        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={prev}
          className="
            absolute
            left-[2%]
            top-1/2
            hidden
            cursor-pointer
            select-none
            rounded-2xl
            lg:block
          "
          style={{
            width: "30%",
            maxWidth: 420,
            transform: "translateY(-50%)",
            background: "var(--card-bg-secondary)",
            border: "1px solid var(--brand-primary)",
            padding: "32px 28px",
            opacity: 0.45,
            maskImage:
              "linear-gradient(to right, transparent, black 40%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 40%)",
          }}
        >
          <Stars count={testimonials[getIndex(-1)].rating} />

          <p
            className="font-dm text-center text-sm leading-relaxed"
            style={{
              color: "var(--text-muted)",
            }}
          >
            {testimonials[getIndex(-1)].text}
          </p>
        </motion.div>

        {/* =======================================================
            CENTER CARD
        ======================================================= */}

        <AnimatePresence mode="wait">
          <motion.div
            key={active + "-card"}
            initial={{
              opacity: 0,
              scale: 0.95,
              x: 40,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              x: -40,
            }}
            transition={{
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              z-10
              w-full
              max-w-[680px]
              rounded-2xl
              border
              border-[var(--border-primary)]
              bg-[var(--card-bg)]
              px-4
              py-6
              text-center
              shadow-2xl
              max-[374px]:px-3
              max-[374px]:py-5
              min-[375px]:px-5
              min-[375px]:py-7
              sm:px-8
              sm:py-9
              md:px-10
              md:py-10
              lg:px-10
              lg:py-11
            "
            style={{
              boxShadow: "0 25px 50px -12px var(--shadow-color)",
            }}
          >
            <Stars count={testimonials[active].rating} />

            <p
              className="
                mx-auto
                max-w-full
                break-words
                font-dm
                text-[12px]
                leading-[1.65]
                max-[374px]:text-[11.5px]
                max-[374px]:leading-[1.65]
                min-[375px]:text-[13px]
                min-[375px]:leading-6
                sm:text-[15px]
                sm:leading-7
                md:text-base
                md:leading-relaxed
              "
              style={{
                color: "var(--text-secondary)",
                overflowWrap: "anywhere",
              }}
            >
              {testimonials[active].text}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* =======================================================
            RIGHT GHOST
        ======================================================= */}

        <motion.div
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={next}
          className="
            absolute
            right-[2%]
            top-1/2
            hidden
            cursor-pointer
            select-none
            rounded-2xl
            lg:block
          "
          style={{
            width: "30%",
            maxWidth: 420,
            transform: "translateY(-50%)",
            background: "var(--card-bg-secondary)",
            border: "1px solid var(--border-secondary)",
            padding: "32px 28px",
            opacity: 0.45,
            maskImage:
              "linear-gradient(to left, transparent, black 40%)",
            WebkitMaskImage:
              "linear-gradient(to left, transparent, black 40%)",
          }}
        >
          <Stars count={testimonials[getIndex(1)].rating} />

          <p
            className="font-dm text-center text-sm leading-relaxed"
            style={{
              color: "var(--text-muted)",
            }}
          >
            {testimonials[getIndex(1)].text}
          </p>
        </motion.div>
      </div>

      {/* =========================================================
          DOT INDICATORS
      ========================================================= */}

      <div
        className="
          mt-5
          flex
          items-center
          justify-center
          gap-2
          max-[374px]:mt-4
          min-[375px]:mt-6
          sm:mt-8
        "
      >
        {testimonials.map((_, i) => (
          <button
            type="button"
            key={i}
            onClick={() => setActive(i)}
            aria-label={`View testimonial ${i + 1}`}
            aria-current={i === active ? "true" : "false"}
            className="
              cursor-pointer
              rounded-full
              border-0
              p-0
              outline-none
              transition-all
              duration-300
              focus-visible:ring-2
              focus-visible:ring-[var(--brand-primary)]
              focus-visible:ring-offset-2
              focus-visible:ring-offset-[var(--bg-primary)]
            "
            style={{
              width: i === active ? 24 : 8,
              height: 8,
              minWidth: i === active ? 24 : 8,
              background:
                i === active
                  ? "var(--brand-primary)"
                  : "color-mix(in srgb, var(--brand-primary) 30%, transparent)",
            }}
          />
        ))}
      </div>
    </section>
  );
}